import React from 'react';
import { EIconTypes } from 'constants/Icons';
import { ICaseManagementTask } from 'models/cases';
import { ITableColumns, ITableDataSource } from 'models/table';
import { formatDate } from 'utils/date';
import { Chips } from 'components/atoms/Chips';
import { Link } from '@mui/material';
import { MenuButton } from 'components/molecules/MenuButton';
import { IDictionary } from 'models/dictionary';
import { findInDictionary } from 'helpers/dictionary';
import { ERouteLinks } from 'models/route';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import { renderStatusCaseFieldColor } from '../helpers';

export enum Elabels {
  caseID = 'caseManagement.cases.caseId.label',
  legalEntityName = 'caseManagement.cases.legalEntityName.label',
  taskName = 'caseManagement.cases.taskName.label',
  licencingAuthority = 'caseManagement.cases.licencingAuthority.label',
  createDate = 'caseManagement.cases.createDate.label',
  status = 'caseManagement.cases.status.label',
  assignedTo = 'caseManagement.cases.assignedTo.label',
  actions = 'caseManagement.cases.actions.label',
  assignedUsersUniqueIds = 'caseManagement.cases.assignedUsers.label',
  applicants = 'caseManagement.cases.applicants.label',
  legalName = 'caseManagement.cases.applicants.label',
  caseStatuses = 'caseManagement.cases.caseStatuses.label',
  caseStatusesUniqueIds = 'caseManagement.cases.caseStatuses.label',
  licenceAuthorities = 'caseManagement.cases.licenceAuthorities.label',
  licenceAuthoritiesUniqueIds = 'caseManagement.cases.licenceAuthorities.label',
  regions = 'caseManagement.cases.regions.label',
  caseIdOrName = 'caseManagement.cases.caseIdOrName.label',
}

export const mapCasesToDataSource = (
  cases: ICaseManagementTask[],
  shortDateFormat: string,
  otherRequestTaskName: IDictionary[] | null,
  weaponLicencingAuthority: IDictionary[] | null,
  weaponLicenceCaseStatus: IDictionary[] | null,
): ITableDataSource[] =>
  cases.map((item) => ({
    id: item.uniqueId,
    caseNumber: item.caseNumber,
    legalName: item.legalName,
    legalUniqueId: item.legalUniqueId,
    legalType: item.legalType,
    taskNameUniqueId: item.taskNameUniqueId,
    taskName: findInDictionary(item.taskNameUniqueId, otherRequestTaskName),
    licencingAuthority: findInDictionary(
      item.licenceAuthorityUniqueId,
      weaponLicencingAuthority,
    ),
    licenceAuthorityUniqueId: item.licenceAuthorityUniqueId,
    createdDate: formatDate(item.createdDate, shortDateFormat),
    statusUniqueId: item.statusUniqueId,
    assignedTo: item.assignedTo,
    assignedToUniqueId: item.assignedToUniqueId,
    status: findInDictionary(item.statusUniqueId, weaponLicenceCaseStatus),
  }));

interface ITasksTableColumns {
  handleShowDetails: (arg: string) => void;
  handleAssign: (arg: string) => void;
  canAssignCase: (licenceAuthorityUniqueId: string, statusUniqueId: string) => boolean;
  t: any;
}

export const tasksTableColumns = ({
  handleShowDetails,
  handleAssign,
  canAssignCase,
  t,
}: ITasksTableColumns): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f000',
    key: 'caseNumber',
    title: Elabels.caseID,
    width: '164px',
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f002',
    key: 'legalName',
    title: Elabels.legalEntityName,
    render: (_data, props) => (
      <Link
        href={`${ERouteLinks.ViewLegalEntity.replace(':id', props.legalUniqueId)}?type=${
          props.legalType === LETypes.individual
            ? ELegalEntityTypes.individual
            : ELegalEntityTypes.organisation
        }`}
        underline="none"
        target="_blank"
        rel="noopener noreferrer"
      >
        {props.legalName}
      </Link>
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f003',
    key: 'taskName',
    title: Elabels.taskName,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f009',
    key: 'licenceAuthorityUniqueId',
    title: Elabels.licencingAuthority,
    render: (_data, props) => props.licencingAuthority,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f004',
    key: 'createdDate',
    title: Elabels.createDate,
    render: (_data, props) => props.createdDate,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f005',
    key: 'statusUniqueId',
    title: Elabels.status,
    render: (_data, props) => (
      <Chips text={props.status} variant={renderStatusCaseFieldColor(props)} fullWidth />
    ),
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f006',
    key: 'assignedTo',
    title: Elabels.assignedTo,
  },
  {
    id: 'f0504ca5-6b32-482d-a169-304a6f32f007',
    key: '',
    title: Elabels.actions,
    isSortable: false,
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <MenuButton
        id={`menu_${props.id}`}
        items={[
          {
            name: t('caseManagement.cases.actions.details.label'),
            icon: EIconTypes.view,
            handler: () => {
              handleShowDetails(props.id);
            },
          },
          ...(canAssignCase(props.licenceAuthorityUniqueId, props.statusUniqueId)
            ? [
                {
                  name: t('caseManagement.cases.actions.assignTo.label'),
                  icon: EIconTypes.assignTo,
                  handler: () => {
                    handleAssign(props.id);
                  },
                },
              ]
            : []),
        ]}
      />
    ),
  },
];
